"use client";

import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { format } from "date-fns";
import i18n from "i18next"; // Import i18n for language context
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import AppLogoIcon from "@/components/LogoIcon";
import { getTimeUntilLesson } from "@/features/workspaces/utils/lessonUtils.ts";
import useUserStore from "@/features/auth/store/useUserStore";
import { Avatar, AvatarImage } from "@/components/ui/avatar.tsx";

interface nextLesson {
  startTime: Date;
  endTime: Date;
  topic: string;
  subject: {
    name: string;
    icon: string;
  };
  tutor: {
    name: string;
  };
  platform: string;
}

export const NextLessonCard = () => {
  const { t } = useTranslation(["workspaces", "common"]);
  const { user } = useUserStore();
  const [nextLesson] = useState<nextLesson | null>(null);

  const timeUntilLesson =
    nextLesson !== null
      ? getTimeUntilLesson(nextLesson.startTime, i18n.language, user?.timezone)
      : "";

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className="bg-foreground/50 relative flex flex-col overflow-hidden rounded-2xl border-none p-4 backdrop-blur-xl sm:p-6"
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.4, delay: 0.3 }}
    >
      {/* Background Circles */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute -top-10 -right-10 h-20 w-20 rounded-full bg-gradient-to-br from-green-400 to-blue-500" />
        <div className="absolute -bottom-14 -left-14 h-28 w-28 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500" />
      </div>

      <div className="relative z-10 flex flex-grow flex-col justify-between">
        <div>
          <div className="mb-3 flex items-center justify-between sm:mb-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <Icon
                className="text-accent-foreground"
                height="1.25rem"
                icon="mdi:calendar-clock"
                width="1.25rem"
              />
              <h3 className="text-accent-foreground text-base font-semibold sm:text-lg">
                {t("workspaces:dashboard.nextLesson.title")}
              </h3>
            </div>
            {nextLesson && (
              <Badge
                className="border-green-500/30 bg-green-500/20 text-xs text-green-400"
                variant="outline"
              >
                {t("workspaces:startsIn")} {timeUntilLesson}
              </Badge>
            )}
          </div>

          {nextLesson ? (
            <div className="bg-muted/20 mb-3 rounded-xl p-3 backdrop-blur-sm sm:mb-4 sm:p-4">
              <h4 className="text-accent-foreground mb-2 text-sm font-semibold sm:text-base">
                {nextLesson.topic}
              </h4>

              <div className="mb-2 flex items-center gap-2 text-xs sm:mb-3 sm:text-sm">
                <Icon
                  className="text-accent-foreground"
                  height="0.875rem"
                  icon={nextLesson.subject.icon}
                  width="0.875rem"
                />
                <span className="text-muted-foreground">{nextLesson.subject.name}</span>
                <span className="text-muted-foreground mx-1">•</span>
                <AppLogoIcon className="size-3.5 sm:size-4" />
                <span className="text-muted-foreground font-medium">{nextLesson.platform}</span>
              </div>

              <div className="mb-2 flex items-center gap-2 text-xs sm:mb-3 sm:text-sm">
                <Avatar className="size-5 sm:size-6">
                  <AvatarImage
                    alt={nextLesson.tutor.name}
                    src={user?.avatarUrl || "/placeholder.svg"}
                  />
                </Avatar>
                <span className="text-muted-foreground">
                  {t("workspaces:dashboard.nextLesson.withTutor", { name: nextLesson.tutor.name })}
                </span>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4">
                <div className="flex items-center gap-2 text-xs sm:text-sm">
                  <Icon
                    className="text-muted-foreground"
                    height="0.875rem"
                    icon="mdi:calendar-range"
                    width="0.875rem"
                  />
                  <span>{format(nextLesson.startTime, "MMM d, yyyy")}</span>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm">
                  <Icon
                    className="text-muted-foreground"
                    height="0.875rem"
                    icon="mdi:clock-outline"
                    width="0.875rem"
                  />
                  <span>
                    {format(nextLesson.startTime, "HH:mm")} - {format(nextLesson.endTime, "HH:mm")}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex h-32 flex-col items-center justify-center text-center sm:h-40">
              <Icon
                className="text-muted-foreground mb-3 sm:mb-4"
                height="2.5rem"
                icon="mdi:calendar-off-outline"
                width="2.5rem"
              />
              <p className="text-muted-foreground text-sm sm:text-base">
                {t("workspaces:dashboard.nextLesson.noUpcomingLessons")}
              </p>
            </div>
          )}
        </div>

        {nextLesson && (
          <Button
            className="w-full text-sm sm:text-base"
            disabled={!nextLesson}
            size="lg"
            variant={"foreground"}
          >
            <Icon className="mr-2" height="1rem" icon="mdi:arrow-right" width="1rem" />
            {t("common:calendar.slot.viewLessonDetails")}
          </Button>
        )}
      </div>
    </motion.div>
  );
};
